import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import CultivarForm from '../../components/cultivar/CultivarForm';
import BreederControls from '../../components/cultivar/BreederControls';
import WebsitePublishPanel from '../../components/cultivar/WebsitePublishPanel';
import SublicensingPanel from '../../components/cultivar/SublicensingPanel';
import { Leaf, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import type { Cultivar, CultivarFormData } from '../../types/cultivar';
import type { ProtectionRow } from '../../components/cultivar/CultivarForm';
import cultivarService from '../../services/cultivar';
import DuplicateCultivarNotice from '../../components/cultivar/DuplicateCultivarNotice';
import { duplicateCultivarOf, type DuplicateCultivar } from '../../utils/cultivarDuplicate';
import { useAuth } from '../../context/AuthContext';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { PLATFORM_ORG_ID } from '../../utils/auth';

function CultivarEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasAccess: canWrite, loading: licenceLoading } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [cultivar, setCultivar] = useState<Cultivar | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateCultivar | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (id && !licenceLoading) {
      loadCultivar(Number(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, licenceLoading, canWrite]);

  const loadCultivar = async (cultivarId: number) => {
    setLoading(true);
    setError(null);

    try {
      const response = await cultivarService.getCultivar(cultivarId);
      if (response.success && response.data) {
        // Per-org editing: pbr or admin licence holder in the creator org.
        const isOwnOrg = response.data.created_by_org_id === user?.organisation_id;
        if (!canWrite || !isOwnOrg) {
          setError('You do not have permission to edit this cultivar. A Cultivar PBR or Admin licence is required, and only the creator organisation can make changes.');
          setCultivar(null);
        } else {
          setCultivar(response.data);
        }
      } else {
        setError(response.message || 'Cultivar not found');
      }
    } catch (err) {
      console.error('Error loading cultivar:', err);
      setError('Failed to load cultivar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (
    data: CultivarFormData,
    protections: ProtectionRow[],
    programIds: number[]
  ) => {
    if (!id) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setDuplicate(null);

    try {
      const cultivarId = Number(id);
      const response = await cultivarService.updateCultivar(cultivarId, data);
      if (response.success && response.data) {
        // Save protections (bulk upsert handles insert/update)
        if (protections.length > 0) {
          await cultivarService.bulkUpsertProtections(cultivarId, protections);
        }
        // Diff program memberships against current cultivar state
        const currentIds = new Set((cultivar?.programs ?? []).map((p) => p.id));
        const nextIds = new Set(programIds);
        const toAdd = [...nextIds].filter((pid) => !currentIds.has(pid));
        const toRemove = [...currentIds].filter((pid) => !nextIds.has(pid));
        for (const pid of toAdd) {
          await cultivarService.addCultivarProgram(cultivarId, pid);
        }
        for (const pid of toRemove) {
          await cultivarService.removeCultivarProgram(cultivarId, pid);
        }
        setSuccess(true);
        // Brief delay to show success state
        setTimeout(() => {
          navigate(`/cultivar/${id}`);
        }, 1500);
      } else {
        setSubmitError(response.message || 'Failed to update cultivar');
      }
    } catch (err) {
      const existing = duplicateCultivarOf(err);
      if (existing) {
        setDuplicate(existing);
      } else {
        console.error('Error updating cultivar:', err);
        setSubmitError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/cultivar/${id}`);
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Edit Cultivar"
          icon={<Leaf className="h-6 w-6 text-white" />}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <Loader2 className="h-10 w-10 text-primary-600 animate-spin mx-auto mb-4" />
              <p className="text-neutral-600">Loading cultivar...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error State (no permission or not found)
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Edit Cultivar"
          icon={<Leaf className="h-6 w-6 text-white" />}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-16 w-16 rounded-full bg-error-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-8 w-8 text-error-600" />
              </div>
              <h2 className="text-xl font-bold text-neutral-900 mb-2">
                Unable to Edit
              </h2>
              <p className="text-neutral-600 mb-6 max-w-md mx-auto">
                {error}
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" onClick={() => navigate('/cultivar')}>
                  Back to Registry
                </Button>
                {id && (
                  <Button variant="primary" onClick={() => navigate(`/cultivar/${id}`)}>
                    View Cultivar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success State
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Edit Cultivar"
          subtitle={cultivar?.name}
          icon={<Leaf className="h-6 w-6 text-white" />}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-16 w-16 rounded-full bg-success-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-success-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                Cultivar Updated Successfully!
              </h2>
              <p className="text-neutral-600">
                Redirecting to cultivar details...
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Edit Form
  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Edit Cultivar"
        subtitle={`Editing: ${cultivar?.name}`}
        icon={<Leaf className="h-6 w-6 text-white" />}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {duplicate && <DuplicateCultivarNotice existing={duplicate} />}

        {/* Submit Error Banner */}
        {submitError && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{submitError}</p>
          </div>
        )}

        <Card>
          <CardContent className="p-6 sm:p-8">
            {cultivar && (
              <CultivarForm
                initialData={cultivar}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                isSubmitting={isSubmitting}
              />
            )}
          </CardContent>
        </Card>

        {/* Breeder controls: synonyms, release state, per-region release */}
        {cultivar && (
          <BreederControls
            cultivarId={cultivar.id}
            isBreederOwner={cultivar.breeder_org_id === user?.organisation_id}
            initialReleaseState={cultivar.release_state ?? null}
            initialPrimaryDisplaySynonymId={cultivar.primary_display_synonym_id ?? null}
            legacyVisibilityGrace={cultivar.legacy_visibility_grace}
          />
        )}

        {/* Public website publish panel — show_on_website toggle + editorial copy.
            Our website shows only our own cultivars until each organisation
            has its own site (docs/tenant-websites-plan.md). */}
        {cultivar && user?.organisation_id === PLATFORM_ORG_ID && (
          <WebsitePublishPanel
            cultivar={cultivar}
            onUpdated={(updated) => setCultivar(updated)}
          />
        )}

        {/* Who other than our own nursery may supply it. Separate decision
            from publishing, and deliberately a separate panel. */}
        {cultivar && <SublicensingPanel cultivar={cultivar} />}

        {/* Audit Notice */}
        <div className="mt-6 bg-neutral-50 border border-neutral-200 rounded-lg p-4">
          <p className="text-sm text-neutral-600">
            <strong>Note:</strong> All changes to this cultivar are logged in the audit trail
            and will be visible in the cultivar's history.
          </p>
        </div>
      </div>
    </div>
  );
}

export default CultivarEdit;
